//Built-in libraries

import { ArchiveBoxIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
//Third party libraries
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { bulkDeleteApplications, updateApplicationStatus } from '@/api/SageAdmin/ClientAccount';
import TableFooter from '@/components/Table/TableFooter';
import Modal from '@/components/UI/Modal';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
import NewUserForm from '@/components/Users/NewUserForm';
import { useSalesApplications } from '@/hooks/ReactQueryHooks/useSalesApplicationQuery';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
//Services
import { authSelector } from '@/store';
//Assets and Miscellaneous
import { formatDate3 } from '@/utils';

enum ApplicationStatus {
  DEMO_SCHEDULED = 'DEMO_SCHEDULED',
  PROPOSAL_SENT = 'PROPOSAL_SENT',
  CONTRACT_SENT = 'CONTRACT_SENT',
  ONBOARDING = 'ONBOARDING',
  NONE = 'NONE',
}

const STATUS_DISPLAY_MAP = {
  [ApplicationStatus.DEMO_SCHEDULED]: 'Demo Scheduled',
  [ApplicationStatus.PROPOSAL_SENT]: 'Proposal Sent',
  [ApplicationStatus.CONTRACT_SENT]: 'Contract Sent',
  [ApplicationStatus.ONBOARDING]: 'Onboarding',
  [ApplicationStatus.NONE]: 'No Status',
} as const;

const STATUS_COLOR_MAP = {
  [ApplicationStatus.DEMO_SCHEDULED]: 'bg-blue-100 text-blue-800 border-blue-200',
  [ApplicationStatus.PROPOSAL_SENT]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  [ApplicationStatus.CONTRACT_SENT]: 'bg-purple-100 text-purple-800 border-purple-200',
  [ApplicationStatus.ONBOARDING]: 'bg-green-100 text-green-800 border-green-200',
  [ApplicationStatus.NONE]: 'bg-gray-100 text-gray-800 border-gray-200',
} as const;

interface StatusDropdownProps {
  value: string;
  onChange: (value: ApplicationStatus) => void;
  disabled: boolean;
  status: string | undefined;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ value, onChange, disabled, status }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4, // Position below the button with a small gap
          left: rect.left,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const getStatusIcon = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case ApplicationStatus.DEMO_SCHEDULED:
        return (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
            />
          </svg>
        );
      case ApplicationStatus.PROPOSAL_SENT:
        return (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        );
      case ApplicationStatus.CONTRACT_SENT:
        return (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
            />
          </svg>
        );
      case ApplicationStatus.ONBOARDING:
        return (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        );
      default:
        return (
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        );
    }
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        ref={buttonRef}
        type='button'
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`inline-flex items-center px-3 py-1.5 border rounded-full text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 ${
          STATUS_COLOR_MAP[status as ApplicationStatus] || STATUS_COLOR_MAP[ApplicationStatus.NONE]
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[color-mix(in_srgb,currentColor_80%,transparent)]'}`}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        <span className='flex items-center gap-2'>
          {getStatusIcon(status)}
          <span>{STATUS_DISPLAY_MAP[value as ApplicationStatus] || 'Select Status'}</span>
          {!disabled && (
            <svg className='w-4 h-4 ml-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
            </svg>
          )}
        </span>
      </button>

      {isOpen &&
        !disabled &&
        createPortal(
          <div
            className='fixed z-[9999] w-48 rounded-md shadow-lg bg-white ring-1 ring-black/5'
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <ul className='py-1 max-h-60 overflow-auto' role='listbox' tabIndex={-1}>
              {Object.entries(STATUS_DISPLAY_MAP).map(([value, label]) => (
                <li
                  key={value}
                  role='option'
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                    value === status ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => {
                    onChange(value as ApplicationStatus);
                    setIsOpen(false);
                  }}
                >
                  {getStatusIcon(value)}
                  {label}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

// Default search parameters
const defaultSearchParams = {
  page: 1,
  perPage: 10,
  orderBy: undefined as string | undefined,
  order: undefined as string | undefined,
  search: undefined as string | undefined,
};

//Component Begin
export default function SalesDemoApplications() {
  const _navigate = useNavigate();
  const _location = useLocation();
  const { data: currentUserData } = useSelector(authSelector);

  const [fetchParams, setFetchParams] = useState(defaultSearchParams);
  const [searchInput, setSearchInput] = useState('');
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const _today = new Date().toISOString();
  const [selectedApplications, setSelectedApplications] = useState<number[]>([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  // Reset to default params when component mounts
  useEffect(() => {
    setFetchParams(defaultSearchParams);
  }, []);

  const fetchSalesApplications = useSalesApplications({
    params: fetchParams,
  });
  const salesApplications = fetchSalesApplications.data?.data;
  const applicationsCount = fetchSalesApplications.data?.meta?.total;
  const totalPage = Math.ceil(applicationsCount / fetchParams.perPage);

  const getAccountTitles = useOrgTitles({});
  const _accountTitles = getAccountTitles?.data?.data;

  const onPageChangeHandler = (direction: string) => {
    setSelectedApplications([]);
    if (direction === 'prev') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page > 1 ? prevState.page - 1 : 1,
      }));
    } else if (direction === 'next') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page === totalPage ? totalPage : prevState.page + 1,
      }));
    }
  };

  const perPageHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFetchParams((prevState) => ({
      ...prevState,
      page: 1,
      perPage: Number(e.target.value),
    }));
  };

  const sortHandler = ({ orderBy, order }: { orderBy: string; order: string }) => {
    setFetchParams((prev) => ({
      ...prev,
      page: 1,
      orderBy,
      order,
    }));
  };

  const handleSearch = () => {
    setFetchParams((prev) => ({
      ...prev,
      page: 1,
      search: searchInput || undefined,
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectApplication = (applicationId: number) => {
    setSelectedApplications((prev) => {
      if (prev.includes(applicationId)) {
        return prev.filter((id) => id !== applicationId);
      }
      return [...prev, applicationId];
    });
  };

  const handleSelectAll = () => {
    if (selectedApplications.length === salesApplications?.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(salesApplications?.map((app: { id: number }) => app.id) || []);
    }
  };

  const handleArchive = async () => {
    if (selectedApplications.length === 0) return;

    setIsArchiving(true);
    try {
      await bulkDeleteApplications(selectedApplications);
      setSelectedApplications([]);
      fetchSalesApplications.refetch();
    } catch (error) {
      console.error('Failed to delete applications:', error);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleStatusChange = async (applicationId: number, newStatus: ApplicationStatus) => {
    if (updatingStatus === applicationId) return;

    setUpdatingStatus(applicationId);
    try {
      // Optimistically update the UI
      const _updatedApplications = salesApplications?.map((app: { id: number; status?: string }) =>
        app.id === applicationId ? { ...app, status: newStatus } : app
      );

      // Make the API call
      await updateApplicationStatus(applicationId, newStatus);

      // Refresh the list to ensure consistency
      fetchSalesApplications.refetch();
    } catch (error) {
      console.error('Failed to update status:', error);
      // Refresh to revert optimistic update
      fetchSalesApplications.refetch();
    } finally {
      setUpdatingStatus(null);
    }
  };

  const _getStatusColor = (status: string | undefined) => {
    return STATUS_COLOR_MAP[status as ApplicationStatus] || STATUS_COLOR_MAP[ApplicationStatus.NONE];
  };

  const _getStatusDisplay = (status: string | undefined) => {
    return STATUS_DISPLAY_MAP[status as ApplicationStatus] || STATUS_DISPLAY_MAP[ApplicationStatus.NONE];
  };

  return (
    <div className='flex flex-col w-full mx-auto'>
      {showNewUserForm && (
        <Modal>
          <NewUserForm formModalHandler={() => setShowNewUserForm((prevState) => !prevState)} />
        </Modal>
      )}
      <div className='align-middle inline-block min-w-full text-sm'>
        <section className='border-t'>
          <div className='flex m-4 gap-4 items-center justify-between'>
            {selectedApplications.length > 0 && (
              <button
                onClick={handleArchive}
                disabled={isArchiving}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ArchiveBoxIcon className='h-5 w-5 mr-2' />
                {isArchiving ? 'Deleting...' : `Delete ${selectedApplications.length} Selected`}
              </button>
            )}
            <div className='relative flex items-center gap-2 ml-auto'>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' />
                </div>
                <input
                  type='text'
                  className='block w-64 pl-9 pr-3 py-1.5 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-hidden focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm'
                  placeholder='Search applications...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
              <button
                onClick={handleSearch}
                className='inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              >
                Search
              </button>
            </div>
          </div>
          {fetchParams.search && <div className='p-4 -mt-4'>{applicationsCount} applications found</div>}
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-100'>
                <tr>
                  <th className='px-6 py-4 sticky left-0 bg-gray-100 z-10'>
                    <input
                      type='checkbox'
                      className='h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded'
                      checked={selectedApplications.length === salesApplications?.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {['Name', 'Email', 'Role', 'Organization Type', 'Selected Challenges', 'Other Challanges', 'Status', 'Submitted At'].map(
                    (head) => (
                      <th
                        key={head}
                        scope='col'
                        className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider whitespace-nowrap'
                      >
                        <div className='flex gap-2 items-center relative'>
                          {head}
                          {head !== 'Status' && (
                            <SimpleDropDown
                              menuButtonClassName='flex text-sm items-start'
                              menuItemClassName='left-0 capitalize font-medium'
                              toggle={<ChevronDownIcon className='h-4 w-4' />}
                            >
                              <p className='px-3 pt-2'>Sort</p>
                              <DropDownItem
                                id='orderByAsc'
                                onClick={() =>
                                  sortHandler({
                                    orderBy: head.toLowerCase(),
                                    order: 'asc',
                                  })
                                }
                              >
                                <span>Ascending</span>
                              </DropDownItem>
                              <DropDownItem
                                id='orderByDesc'
                                onClick={() =>
                                  sortHandler({
                                    orderBy: head.toLowerCase(),
                                    order: 'desc',
                                  })
                                }
                              >
                                <span>Descending</span>
                              </DropDownItem>
                            </SimpleDropDown>
                          )}
                        </div>
                      </th>
                    )
                  )}
                </tr>
              </thead>
              {fetchSalesApplications.isFetching && (
                <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
                  {[0, 1, 2, 3, 4, 5].map((num) => (
                    <tr key={num}>
                      <td className='p-6 sticky left-0 bg-white z-10'>
                        <div className='h-4 w-4 bg-gray-200 rounded' />
                      </td>
                      <td className='p-6'>
                        <div className='flex gap-2 items-center'>
                          <span className='h-10 w-10 rounded-full bg-neutralColor-light block' />
                          <div className='space-y-2'>
                            <span className='h-2 rounded-md bg-neutralColor-light w-32 block' />
                            <span className='h-2 rounded-md bg-neutralColor-light w-20 block' />
                          </div>
                        </div>
                      </td>
                      <td className='p-6'>
                        <span className='h-2 rounded-md bg-neutralColor-light w-32 block' />
                      </td>
                      <td className='p-6'>
                        <span className='h-6 rounded-full bg-neutralColor-light w-20 block' />
                      </td>
                      <td className='p-6'>
                        <span className='h-2 rounded-md bg-neutralColor-light w-32 block' />
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
              {!fetchSalesApplications.isFetching && (
                <tbody className='divide-y w-full'>
                  {fetchSalesApplications.isSuccess &&
                    salesApplications?.map(
                      (application: {
                        id: number;
                        name: string;
                        email: string;
                        primaryRole: string;
                        organizationType: string;
                        selectedChallenges: string[];
                        otherChallenges: string;
                        status?: string;
                        createdAt: string;
                      }) => {
                        if (application.id !== currentUserData.id) {
                          return (
                            <tr
                              key={application.id}
                              className={`hover:bg-green-50 cursor-pointer ${
                                selectedApplications.includes(application.id) ? 'bg-green-50' : ''
                              }`}
                            >
                              <td className='px-6 py-4 sticky left-0 bg-white z-10'>
                                <input
                                  type='checkbox'
                                  className='h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded'
                                  checked={selectedApplications.includes(application.id)}
                                  onChange={() => handleSelectApplication(application.id)}
                                />
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                <div className='text-sm text-neutralColor-normal'>{application.name}</div>
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                <div className='text-sm text-neutralColor-normal'>{application.email}</div>
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                <div className='text-sm text-neutralColor-normal'>{application?.primaryRole}</div>
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>{application.organizationType}</td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                                {application.selectedChallenges.length > 0 ? (
                                  <ul className='list-disc list-inside space-y-1'>
                                    {application.selectedChallenges.map((challenge, idx) => (
                                      <li key={`challenge-${idx}`}>{challenge}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className='text-gray-300 italic'>None</span>
                                )}
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>{application.otherChallenges}</td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                <StatusDropdown
                                  value={application.status || ApplicationStatus.NONE}
                                  onChange={(newStatus) => handleStatusChange(application.id, newStatus)}
                                  disabled={updatingStatus === application.id}
                                  status={application.status}
                                />
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>{formatDate3(application?.createdAt)}</td>
                            </tr>
                          );
                        }
                        return null;
                      }
                    )}
                </tbody>
              )}
            </table>
          </div>
          <TableFooter
            currentPage={fetchParams.page}
            totalPage={totalPage}
            perPageHandler={perPageHandler}
            pageChangeHandler={onPageChangeHandler}
          />
        </section>
      </div>
    </div>
  );
}
